import {storeInstance} from '@aofl/store/dist/legacy';
import {
  sdoNs,
  pages,
  permissions, ssoErrors, apiNamespaces, landingPageProducts, products, productOrder
} from './constants-enumerate';
import {deepAssign} from '@aofl/object-utils';
import {ApiService, apiRequestInstance} from './api-service';
import {routerInstance} from '@aofl/router';
import {userJobs} from './user-jobs';
import {hasOneOfPermissions} from './permissions';
import {userTypes} from '@modules/constants-enumerate';
import {Toast} from '@modules/toast';
import {unsafeHTML} from 'lit-html/directives/unsafe-html';
import {AnalyticEvent, AnalyticsTrackerQueue} from '@epg/epg-components';
import {DeviceInfo} from '@modules/device-info';
import {resourceEnumerateInstance} from '@modules/resource-enumerate-instance';
import {CurrentProduct} from '@epg/epg-components';

const DEFAULT_SETTINGS = {
  tier_structure: {
    levels: '0',
    labels: []
  },
  search_reports: [],
  hide_sections: {},
  third_party_rostering_status: 'disconnected',
  available_rostering_systems: {
    clever: false,
    classlink: false,
  },
};

const DEFAULT_STATE = {
  roles: [],
  settings: DEFAULT_SETTINGS,
  languages: [],
  current_role: {
    id: 0,
    group_id: 0,
    access_data: {},
    group_name: ''
  },
  group_info: {
    id: 0,
    name: '',
    parent_id: 0,
    external_id: '',
    meta_data: [],
    type: '',
    status: '',
    level: 0,
    phone: null,
    path: '',
  },
  id: 0,
  currentLevel: 0,
  childLevel: 0,
  selectedGroup: {},
  type: '',
  name: '',
  email: '',
  phone: '',
  status: '',
  current_role_id: null,
  loggedIn: false,
  locale: 'en-US',
  products: [],
  loginToken: null,
  parent_products: [],
  profiles: [],
  current_profile: {
    id: 0,
    user_id: 0,
    root_id: 0,
    first_name: '',
    last_name: '',
    contact_email: '',
    contact_phone: '',
    group_name: '',
  },
  conflicts: null,
  last_out_of_quantity_acknowledged: 0,
  last_out_of_quantity_info: [],
  successful_roster: false,
  firstTimeLogin: false,
  product_active_students: {},
};

const sdo = {
  namespace: sdoNs.SESSION,
  mutations: {
    init(subState) {
      return Object.assign({}, DEFAULT_STATE, {
        locale: subState?.locale || DEFAULT_STATE.locale,
        geoipCountry: subState?.geoipCountry,
      });
    },
    setSelectedGroup(subState, selectedGroup) {
      const ret = {
        ...subState,
        selectedGroup
      };
      localStorage.setItem('group_id', selectedGroup.id);
      if (selectedGroup.groups?.length) {
        ret.childLevel = selectedGroup.groups[0].level;
      }
      return ret;
    },
    setData(subState, {current_role, group_info, roles, settings, email, phone, name, first_name, last_name, status, type, loggedIn, id, currentLevel, products, productActivations, languages, third_party_connected_group, parent_products, profiles, current_profile, conflicts, last_out_of_quantity_acknowledged, last_out_of_quantity_info, successful_roster, product_active_students}) { // eslint-disable-line camelcase
      return Object.assign({}, subState, {
        current_role,
        group_info,
        roles,
        settings: Object.assign({}, DEFAULT_SETTINGS, settings),
        email,
        phone,
        name: current_profile.name,
        first_name,
        last_name,
        status,
        type,
        loggedIn,
        currentLevel,
        childLevel: currentLevel + 1,
        id,
        productActivations: productActivations || subState.productActivations,
        products,
        languages,
        third_party_connected_group,
        parent_products,
        profiles,
        current_profile,
        conflicts,
        last_out_of_quantity_acknowledged,
        last_out_of_quantity_info,
        successful_roster,
        selectedGroup: group_info,
        product_active_students,
      });
    },
    setReports(subState, searchReports) { // eslint-disable-line
      return deepAssign(subState, 'settings', {search_reports: [...searchReports]});
    },
    setTimezone(subState, timezone) {
      return deepAssign(subState, 'settings', {timezone});
    },
    setStandards(subState, standardSetIds) {
      return deepAssign(subState, 'settings', {external_standards: standardSetIds});
    },
    logout(substate) {
      return Object.assign({}, substate, DEFAULT_STATE);
    },
    setProductActivations(subState, productActivations) {
      return Object.assign({}, subState, {productActivations});
    },
    clearProductActivations(substate) {
      return Object.assign({}, substate, {
        productActivations: null
      });
    },
  },
  decorators: [
    (nextState) => { // $myGroups
      const state = storeInstance.getState();
      const nextSessionState = nextState[sdoNs.SESSION];
      const currentSessionState = state[sdoNs.SESSION];
      if (
        typeof nextSessionState.$myGroups === 'undefined'
        ||
        nextSessionState.settings?.tier_structure !== currentSessionState.settings?.tier_structure
      ) {
        const $myGroups = {};

        const labels = nextSessionState.settings?.tier_structure?.labels || []; // eslint-disable-line camelcase

        for (let i = 0; i < labels.length; i++) {
          const label = nextSessionState.settings.tier_structure.labels[i];
          const level = i + 2;
          $myGroups[level] = {
            label,
            level
          };
        }

        nextState = deepAssign(nextState, `${sdoNs.SESSION}`, {
          $myGroups
        });
      }

      return nextState;
    },
    (nextState) => { // $roles
      const state = storeInstance.getState();
      const nextSessionState = nextState[sdoNs.SESSION];
      const currentSessionState = state[sdoNs.SESSION];
      if (
        nextSessionState.current_role !== currentSessionState.current_role
        ||
        nextSessionState.roles !== currentSessionState.roles
      ) {
        const roles = [];
        for (let i = 0; i < nextSessionState.roles.length; i++) {
          const role = Object.assign({}, nextSessionState.roles[i], {$active: false});
          if (nextSessionState.current_role.id === role.id) {
            role.$active = true;
          }
          roles.push(role);
        }

        nextState = deepAssign(nextState, `${sdoNs.SESSION}`, {roles});
      }

      return nextState;
    },
    (nextState) => { // $profiles, $showSwitcher, $orgs
      const state = storeInstance.getState();
      const nextSessionState = nextState[sdoNs.SESSION];
      const currentSessionState = state[sdoNs.SESSION];
      if (
        nextSessionState.profiles !== currentSessionState.profiles ||
        nextSessionState.current_profile !== currentSessionState.current_profile ||
        nextSessionState.current_role !== currentSessionState.current_role
      ) {
        const profiles = [];
        const $orgs = {};
        let $showSwitcher = false;
        if (nextSessionState.type !== userTypes.PARENT) {
          for (let i = 0; i < nextSessionState.profiles.length; i++) {
            const profile = Object.assign({}, nextSessionState.profiles[i], {$active: false});
            if (nextSessionState.current_profile.id === profile.id) {
              profile.$active = true;
            }
            $orgs[profile.root_id] = profile.group_name;
            profiles.push(profile);
          }
          $showSwitcher = Object.keys($orgs).length > 1;
        }
        nextState = deepAssign(nextState, `${sdoNs.SESSION}`, {profiles, $orgs, $showSwitcher});
      }

      return nextState;
    },
    (nextState) => { // $topLevel, $classLevel
      const state = storeInstance.getState();
      const nextSessionState = nextState[sdoNs.SESSION];
      const subState = state[sdoNs.SESSION];

      if (typeof nextSessionState.$classLevel === 'undefined' ||
        nextSessionState.settings.tier_structure.levels !== subState.settings.tier_structure.levels) {
        const $topLevel = 1;
        const $levels = parseInt(nextSessionState.settings.tier_structure.levels, 10) || 1;
        const $classLevel = $levels + 1 || 2;
        nextState = deepAssign(nextState, sdoNs.SESSION, {
          $levels,
          $topLevel,
          $classLevel
        });
      }

      return nextState;
    },
    (nextState) => { // $activatedProductsStatus
      const state = storeInstance.getState();
      const nextSessionState = nextState[sdoNs.SESSION];
      const subState = state[sdoNs.SESSION];

      if (
        typeof subState.$activatedProductsStatus === 'undefined' ||
        nextSessionState.productActivations !== subState.productActivations
      ) {
        let $activatedProductsStatus = null;
        if (nextSessionState.productActivations) {
          $activatedProductsStatus = nextSessionState.productActivations.has_errors ? 'error' : 'success';
        }

        nextSessionState.$activatedProductsStatus = $activatedProductsStatus;
      }

      return nextState;
    },
    (nextState) => { // $unactivatedProducts
      const state = storeInstance.getState();
      const nextSessionState = nextState[sdoNs.SESSION];
      const subState = state[sdoNs.SESSION];

      if (
        typeof subState.$unactivatedProducts === 'undefined' ||
        nextSessionState.products !== subState.products
      ) {
        nextSessionState.$unactivatedProducts = nextSessionState.products.filter((product) => {
          return product.activation_status === 'unactivated';
        });
      }

      return nextState;
    },
    (nextState) => { // $conflictProducts
      const state = storeInstance.getState();
      const nextSessionState = nextState[sdoNs.SESSION];
      const subState = state[sdoNs.SESSION];

      if (
        typeof subState.$conflictProducts === 'undefined'
        ||
        nextSessionState.products !== subState.products
      ) {
        nextSessionState.$conflictProducts = nextSessionState.products.filter((product) => {
          return product.activation_status === 'conflict';
        });
      }

      return nextState;
    },
    (nextState) => { // $errorProducts
      const state = storeInstance.getState();
      const nextSessionState = nextState[sdoNs.SESSION];
      const subState = state[sdoNs.SESSION];

      if (
        typeof subState.$errorProducts === 'undefined'
        ||
        nextSessionState.products !== subState.products
      ) {
        nextSessionState.$errorProducts = nextSessionState.products.filter((product) => {
          return product.activation_status === 'error';
        });
      }

      return nextState;
    },
    (nextState) => { // $showLanding, $landingPageProducts, $routeToLanding
      const state = storeInstance.getState();
      const nextSessionState = nextState[sdoNs.SESSION];
      const subState = state[sdoNs.SESSION];

      if (
        typeof subState.$showLanding === 'undefined'
        ||
        nextSessionState.settings.aofl_products !== subState.settings.aofl_products
      ) {
        let $showLanding = false;
        let $landingPageProducts = [];
        let $routeToLanding = false;

        if (nextSessionState.settings.aofl_products !== undefined) {
          for (let product of landingPageProducts) {
            let has_product = nextSessionState.settings.aofl_products[product] !== undefined ? nextSessionState.settings.aofl_products[product] : false;
            if (has_product) {
              $showLanding = true;
              $routeToLanding = true;
              $landingPageProducts.push(product);
            }
          }

          if (nextSessionState.settings.aofl_products[products.MATH_ACADEMY]) {
            $routeToLanding = false;
          }
        }

        nextState = deepAssign(nextState, sdoNs.SESSION, {$showLanding, $landingPageProducts, $routeToLanding});
      }

      return nextState;
    },
    (nextState) => { // $isTpRosteringConnected
      const state = storeInstance.getState();
      const subState = state[sdoNs.SESSION];
      const nextSessionState = nextState[sdoNs.SESSION];

      if (
        typeof nextSessionState.$isTpRosteringConnected === 'undefined'
        ||
        typeof nextSessionState.$tprSettings === 'undefined'
        ||
        nextSessionState.third_party_connected_group !== subState.third_party_connected_group
      ) {
        const $tprSettings = nextSessionState.third_party_connected_group || {};
        const tpRosteringStatus = $tprSettings.third_party_rostering_status;
        const $isTpRosteringConnected = (typeof tpRosteringStatus !== 'undefined' && tpRosteringStatus !== 'disconnected');
        nextState = deepAssign(nextState, sdoNs.SESSION, {$isTpRosteringConnected, $tprSettings});
      }
      return nextState;
    },
    (nextState) => { // $activeAoflProducts
      const state = storeInstance.getState();
      const subState = state[sdoNs.SESSION];
      const nextSessionState = nextState[sdoNs.SESSION];
      if (typeof nextSessionState.settings.aofl_products !== 'undefined' &&
        (typeof nextSessionState.$activeAoflProducts === 'undefined'
          || nextSessionState.settings.aofl_products !== subState.settings.aofl_products)) {
        const $activeAoflProducts = [];
        for (let i = 0; i < productOrder.length; i++) {
          const product = productOrder[i];
          if (nextSessionState.settings.aofl_products[product]) {
            $activeAoflProducts.push(product);
          }
        }
        nextState = deepAssign(nextState, sdoNs.SESSION, {$activeAoflProducts});
      }
      return nextState;
    },
    (nextState) => { // $childGroupLabel
      const nextSubState = nextState[sdoNs.SESSION];
      const state = storeInstance.getState();
      const subState = state[sdoNs.SESSION];
      let $childGroupLabel = '';
      let $nonPluralChildGroupLabel = '';
      if (typeof nextSubState.$childGroupLabel === 'undefined' ||
        nextSubState.settings?.tier_structure !== subState.settings?.tier_structure ||
        nextSubState.selectedGroup !== subState.selectedGroup
      ) {
        const childLevel = nextSubState.selectedGroup?.path?.split('.')?.length + 1;
        $childGroupLabel = nextSubState.$myGroups[childLevel]?.label;

        if (!$childGroupLabel) {
          $childGroupLabel = (nextSubState.currentLevel === nextSubState.$classLevel) ?
            'Students' :
            'Classes';
        }
        const lastCharacter = $childGroupLabel.charAt($childGroupLabel.length - 1);

        if ($childGroupLabel === 'Classes') {
          $nonPluralChildGroupLabel = 'Class';
        } else if (lastCharacter === 's') {
          $nonPluralChildGroupLabel = $childGroupLabel.substr(0, $childGroupLabel.length - 1);
        } else {
          $nonPluralChildGroupLabel = $childGroupLabel;
        }
        nextState = deepAssign(nextState, sdoNs.SESSION, {
          $childGroupLabel,
          $nonPluralChildGroupLabel
        });
      }
      return nextState;
    },
    (nextState) => { // $hasLicenseConflicts
      const state = storeInstance.getState();
      const subState = state[sdoNs.SESSION];
      const nextSessionState = nextState[sdoNs.SESSION];

      if (nextSessionState.conflicts !== subState.conflicts) {
        nextState = deepAssign(nextState, sdoNs.SESSION, {$hasLicenseConflicts: !!nextSessionState.conflicts});
      }

      return nextState;
    },
    (nextState) => { // $isOutOfLicense
      const state = storeInstance.getState();
      const subState = state[sdoNs.SESSION];
      const nextSessionState = nextState[sdoNs.SESSION];

      if (
        nextSessionState.last_out_of_quantity_acknowledged !== subState.last_out_of_quantity_acknowledged
        ||
        nextSessionState.last_out_of_quantity_info !== subState.last_out_of_quantity_info
      ) {
        const isOutOfLicense = nextSessionState.last_out_of_quantity_acknowledged < nextSessionState.last_out_of_quantity_info?.timestamp;
        nextState = deepAssign(nextState, sdoNs.SESSION, {$isOutOfLicense: isOutOfLicense, outOfQuantityLicenseName: nextSessionState.last_out_of_quantity_info?.license_name, currentMinimumQuantity: nextSessionState.last_out_of_quantity_info?.quantity});
      }

      return nextState;
    },
    (nextState) => { // $hasExternalStandards
      const state = storeInstance.getState();
      const subState = state[sdoNs.SESSION];
      const nextSessionState = nextState[sdoNs.SESSION];

      if (
        typeof nextSessionState.$hasExternalStandards === 'undefined' ||
        nextSessionState.settings?.external_standards !== subState.settings?.external_standards
      ) {
        const $hasExternalStandards = (nextSessionState.settings?.external_standards?.length > 0);
        nextState = deepAssign(nextState, sdoNs.SESSION, {$hasExternalStandards});
      }

      return nextState;
    },
    (nextState) => { // $isRootAdmin
      const state = storeInstance.getState();
      const subState = state[sdoNs.SESSION];
      const nextSessionState = nextState[sdoNs.SESSION];

      if (
        typeof nextSessionState.$isRootAdmin === 'undefined' ||
        nextSessionState.current_profile?.root_id !== subState.current_profile?.root_id ||
        nextSessionState.current_role?.group_id !== subState.current_role?.group_id
      ) {
        const $isRootAdmin = (nextSessionState.current_profile?.root_id > 0) &&
          (nextSessionState.current_role?.group_id === nextSessionState.current_profile?.root_id);
        nextState = deepAssign(nextState, sdoNs.SESSION, {$isRootAdmin});
      }
      return nextState;
    },
    (nextState) => { // $orgType
      const state = storeInstance.getState();
      const subState = state[sdoNs.SESSION];
      const nextSessionState = nextState[sdoNs.SESSION];

      if (
        typeof nextSessionState.$orgType === 'undefined' ||
        (
          nextSessionState.settings?.group_settings?.organization_type.length > 0 &&
          subState.settings?.group_settings?.organization_type.length > 0 &&
          nextSessionState.settings?.group_settings?.organization_type[0] !== subState.settings?.group_settings?.organization_type[0]
        )
      ) {
        const $orgType = nextSessionState.settings?.group_settings?.organization_type[0];
        nextState = deepAssign(nextState, sdoNs.SESSION, {$orgType});
      }
      return nextState;
    },
  ]
};
storeInstance.addState(sdo);

class Actions {
  static async logout(callLogout = true, navigate = true) {
    try {
      const event = AnalyticEvent.create('account.logout', {});

      await AnalyticsTrackerQueue.enqueueEvent(event);
      await AnalyticsTrackerQueue.sendQueue();

      if (callLogout) {
        await ApiService.logout();
      }

      // clear resource enumerate cache for maintenance mode
      await resourceEnumerateInstance.get(apiNamespaces.EMS, false);
      userJobs.pause();
      apiRequestInstance.purgeCache();
      window.sessionStorage.clear();
      CurrentProduct.clear();
      storeInstance.purge();

      if (window.zE) {
        if (window.zE.logout) {
          window.zE.logout();
        }
        // reset tags
        window.zE('webWidget', 'updateSettings', {
          webWidget: {
            contactForm: {
              tags: [],
            }
          }
        });
      }

      storeInstance.commit({
        namespace: sdoNs.SESSION,
        mutationId: 'init'
      });

      if (navigate) {
        await routerInstance.navigate(pages.HOME, {});
        location.reload();
      }
    } catch (e) {
      console.error(e);
    }

    window.Appcues && window.Appcues.identify(0, {
      type: '',
      name: '',
      email: ''
    });

    window.dispatchEvent(new CustomEvent('permissions-updated'));
  }

  static async validate() {
    try {
      const data = await ApiService.userValidate();
      storeInstance.commit({
        namespace: sdoNs.SESSION,
        mutationId: 'setData',
        payload: {
          ...data.user_info,
          group_info: data.group_info,
          loggedIn: true,
          currentLevel: data.group_info.level,
          roles: data.roles,
          settings: data.settings,
          current_role: data.current_role,
          products: data.products,
          languages: data.languages,
          third_party_connected_group: data.third_party_connected_group,
          parent_products: data.parent_products,
          profiles: data.user_profiles,
          current_profile: data.current_profile,
          conflicts: data.conflicts,
          last_out_of_quantity_acknowledged: data.last_out_of_quantity_acknowledged,
          last_out_of_quantity_info: data.last_out_of_quantity_info,
          successful_roster: data.successful_roster,
          product_active_students: data.product_active_students,
        }
      });
      localStorage.setItem('root_id', data.current_profile.root_id);
      if (data.user_info.type !== userTypes.PARENT) {
        if (userJobs.interval === null) {
          userJobs.getJobs();
        }


        ApiService.rosterJobGetList()
          .then((rosterJobs) => {
            storeInstance.commit({
              namespace: sdoNs.USER_JOBS,
              mutationId: 'setRosterJobs',
              payload: rosterJobs.jobs
            });
          });
      } else {
        storeInstance.commit({
          namespace: sdoNs.PARENT_INFO,
          mutationId: 'setData',
          payload: {
            ...data.user_info,
            roles: data.roles,
            settings: data.settings,
            current_role: data.current_role,
            products: data.products,
            languages: data.languages,
            third_party_connected_group: data.third_party_connected_group,
            parent_products: data.parent_products,
            new_activations: data.new_activations
          }
        });
      }

      if (data.sso_error) {
        let toastMessage = '';
        switch (data.sso_error) {
          case ssoErrors.DOCEBO_DISABLED:
          case ssoErrors.PROFESSIONAL_LEARNING_DISABLED:
            toastMessage = i18n.__('<tt-EZT2y1DK>', 'Your organization does not currently have access to the Professional Development Center. Please reach out to your Customer Success Manager if this is a mistake.');
            break;
          case ssoErrors.GENERIC:
          default:
            toastMessage = unsafeHTML(await i18n.__('<tt-Lc3tJD5/>', 'Something went wrong. Try launching the Professional Development Center again or reaching out to <a href="mailto:educate@aofl.com" target="_blank">educate@aofl.com</a> if the issue persists.'));
            break;
        }

        Toast.error(await toastMessage);
      }

      Actions.appcuesIdentify(data);

      window.dispatchEvent(new CustomEvent('permissions-updated'));
      return true;
    } catch (e) {
      window.dispatchEvent(new CustomEvent('permissions-updated'));
      return false;
    }
  }

  static async login(email, password, mfaRemember = false, mfaMethod = '', mfaToken = '') {
    try {
      const data = await ApiService.login(email, password, mfaRemember, mfaMethod, mfaToken);

      if (data.session === null) {
        // mfa required
        return data;
      }

      await this.setSession(data, password);

      const state = storeInstance.getState();
      const locale = state[sdoNs.LOCALE_DD];
      const detectedDeviceType = DeviceInfo.getDeviceType();
      let deviceType;
      switch (detectedDeviceType) {
        case 'desktop':
          deviceType = 'Desktop';
          break;
        case 'mobile':
        case 'tablet':
          deviceType = 'Handheld';
          break;
        default:
          deviceType = 'Unknown';
      }

      const deviceInfo = {
        device_uuid: AnalyticsTrackerQueue.getDeviceId(),
        device_type: deviceType,
        operating_system: DeviceInfo.getOS(),
        resolution: screen.width +'x'+ screen.height,
      };

      const event = AnalyticEvent.create('ems.login', {
        success: true,
        user_id: data.session.user_info.id,
        geo_info: locale.ipInfo,
        device_info: deviceInfo
      });

      await AnalyticsTrackerQueue.enqueueEvent(event);

      return data;
    } catch (e) {
      window.dispatchEvent(new CustomEvent('permissions-updated'));
      throw e;
    }
  }

  static async validateToken(token, url = pages.DASHBOARD) {
    const state = storeInstance.getState();
    try {
      // logout of current session before starting a new one
      if (state[sdoNs.SESSION].loggedIn) {
        await ApiService.logout();
        userJobs.pause();
        apiRequestInstance.purgeCache();
        window.sessionStorage.clear();
        storeInstance.purge();
      }
      const data = await ApiService.loginByToken(token);

      await this.setSession(data);
      routerInstance.navigate(url, {});

      return data;
    } catch (e) {
      window.dispatchEvent(new CustomEvent('permissions-updated'));
      throw e;
    }
  }

  static async ssoLogin(provider, code) {
    try {
      const data = await ApiService.ssoLogin(provider, code);

      await this.setSession(data);

      return data;
    } catch (e) {
      window.dispatchEvent(new CustomEvent('permissions-updated'));
      throw e;
    }
  }

  static async setSession(data, password = false) {
    const userData = (data.session) ? data.session : data;
    const states = [
      {
        namespace: sdoNs.SESSION,
        mutationId: 'setData',
        payload: {
          ...userData.user_info,
          group_info: userData.group_info,
          loggedIn: true,
          roles: userData.roles,
          settings: userData.settings,
          current_role: userData.current_role,
          productActivations: userData.product_activations,
          products: userData.products,
          languages: userData.languages,
          parent_products: userData.parent_products,
          profiles: userData.user_profiles,
          current_profile: userData.current_profile,
          product_active_students: userData.product_active_students,
        },
      },
    ];
    localStorage.setItem('group_id', userData.current_role.group_id);

    if (userData.user_info?.type === userTypes.PARENT) {
      states.push({
        namespace: sdoNs.PARENT_INFO,
        mutationId: 'setData',
        payload: {password, firstLogin: true},
      });
    }

    storeInstance.commit(...states);

    // clear resource enumerate cache for maintenance mode
    await resourceEnumerateInstance.get(apiNamespaces.EMS, false);

    Actions.appcuesIdentify(userData);

    window.dispatchEvent(new CustomEvent('permissions-updated'));

    if (window.zE) {
      const identify = {
        name: userData.user_info.name,
        email: userData.user_info.email,
        organization: userData.group_info.name,
      };
      window.zE('webWidget', 'identify', identify);
    }
  }

  static appcuesIdentify(data) {
    const userType = (hasOneOfPermissions([permissions.SPONSOR, permissions.HIDE_STUDENT]) ? 'sponsor' : data.user_info?.type);
    window.Appcues && window.Appcues.identify(data.user_info.id, {
      type: userType, name: data.user_info.name,
      groupName: data.group_info.name, rootGroupName: data.group_info.root_name,
      groupLevel: data.group_info.level, groupTiersCount: data.settings.levels,
      hasRosterData: data.settings.has_roster_data,
      lastLogin: (new Date().getTime())
    });
  }
}

export default Actions;
